import type { Side, Stance } from '../pose/landmarks';
import type { PunchType, Target } from '../recog/events';

/** Top-down ring coordinates in shoulder widths. +z is toward the opponent. */
export interface RingPos {
  x: number;
  z: number;
}

export type AiPosture = 'neutral' | 'guard-high' | 'guard-tight' | 'slip-left' | 'slip-right' | 'duck' | 'roll' | 'pull';

/** A punch the AI has committed to; becomes an incoming threat for the player. */
export interface IncomingAttack {
  id: number;
  type: PunchType;
  side: Side;
  target: Target;
  /** ms. Wind-up starts here — this is when the player can first *see* it. */
  tTelegraph: number;
  /** ms. The instant it would land. Player defence is evaluated against this. */
  tImpact: number;
  /** ms. Hand back in guard; the counter window closes here. */
  tRecover: number;
  /** Lateral line of the punch in the player's frame, sw. */
  lane: number;
  /** Height of the punch in the player's frame relative to their shoulder line, sw. */
  height: number;
  power: number;
  /** 0..1. Feints never land; the player is meant to react to them anyway. */
  isFeint: boolean;
  resolved: boolean;
}

export type HitOutcome = 'clean' | 'glancing' | 'blocked' | 'slipped' | 'miss';

export interface HitResult {
  outcome: HitOutcome;
  /** 0..1 combined hit probability before thresholding. Drives damage and score. */
  quality: number;
  /** Why it landed or did not — surfaced in the coaching overlay. */
  terms: {
    range: number;
    lateral: number;
    height: number;
    defence: number;
    opening: number;
    confidence: number;
  };
  /** Set when the defender's action is what stopped it. */
  defeatedBy?: string;
}

export interface FighterVitals {
  /** 0..100. Not "health" — it is the ability to keep composure under fire. */
  poise: number;
  /** 0..100. Drains with output and movement, gates punch speed and guard quality. */
  stamina: number;
  /** Count of knockdowns this session. */
  downs: number;
}

export interface PlayerSimState {
  ring: RingPos;
  stance: Stance;
  vitals: FighterVitals;
  /** Smoothed guard quality per hand, 0..1. */
  guard: Record<Side, number>;
  shell: number;
}

/**
 * `punch-out` — fixed range, upper body only. The loop is
 * telegraph → read → dodge → punish. Distance is removed from gameplay entirely,
 * which is the right call for a phone camera: apparent-size depth was the least
 * trustworthy signal in the whole system, and footwork needs floor space most
 * people do not have. What remains — head displacement and arm extension in
 * torso space — is what the camera measures *well*.
 *
 * `free` — the original range-managed sparring. Retained and still tested, but
 * not exposed in the UI.
 */
export type MatchMode = 'punch-out' | 'free';

export interface MatchConfig {
  mode: MatchMode;
  roundSeconds: number;
  rounds: number;
  restSeconds: number;
  /** Nominal gap between fighters at neutral, sw. Tuned so a full jab just reaches. */
  neutralGap: number;
  /** Whether poise loss can end a round. Off in drill modes. */
  knockdowns: boolean;
}

export const DEFAULT_MATCH: MatchConfig = {
  mode: 'punch-out',
  roundSeconds: 120,
  rounds: 3,
  restSeconds: 40,
  neutralGap: 1.32,
  knockdowns: true,
};

/** How exposed the opponent is right now. Drives both hit resolution and the art. */
export interface Openness {
  /** 0 = hands up and set, 1 = wide open. */
  value: number;
  /** ms remaining in the punish window, 0 when closed. */
  remainingMs: number;
  /** Why it is open — shown to the player so the causal link is unmissable. */
  reason: 'dodge' | 'perfect-dodge' | 'counter' | 'fatigue' | 'none';
}
